import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, Copy, Code } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function APIFormatter() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("beautify");
  
  // Beautify
  const [beautifyInput, setBeautifyInput] = useState("");
  const [beautifyOutput, setBeautifyOutput] = useState("");
  const [beautifyFormat, setBeautifyFormat] = useState("json");
  
  // Flatten
  const [flattenInput, setFlattenInput] = useState("");
  const [flattenOutput, setFlattenOutput] = useState("");
  
  // Generate Types
  const [typesInput, setTypesInput] = useState("");
  const [typesOutput, setTypesOutput] = useState("");
  const [typesLanguage, setTypesLanguage] = useState("typescript");

  const handleBeautify = async () => {
    if (!beautifyInput.trim()) {
      toast.error("Please enter content to beautify");
      return;
    }
    try {
      const response = await axios.post(`${API}/formatter/beautify`, {
        content: beautifyInput,
        format_type: beautifyFormat
      });
      setBeautifyOutput(response.data.beautified);
      toast.success("Content beautified!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to beautify");
    }
  };

  const handleFlatten = async () => {
    if (!flattenInput.trim()) {
      toast.error("Please enter JSON to flatten");
      return;
    }
    try {
      const response = await axios.post(`${API}/formatter/flatten`, {
        content: flattenInput,
        format_type: "json"
      });
      setFlattenOutput(response.data.flattened);
      toast.success(`Flattened into ${response.data.key_count} keys`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to flatten");
    }
  };

  const handleGenerateTypes = async () => {
    if (!typesInput.trim()) {
      toast.error("Please enter JSON to generate types");
      return;
    }
    try {
      const response = await axios.post(`${API}/formatter/generate-types`, {
        json_content: typesInput,
        language: typesLanguage
      });
      setTypesOutput(response.data.generated);
      toast.success(`${typesLanguage} interface generated!`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to generate types");
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#0a0a0b] via-[#0f0f12] to-[#0a0a0b]">
      {/* Header */}
      <div className="border-b border-zinc-800/50 backdrop-blur-xl bg-zinc-900/30">
        <div className="max-w-7xl mx-auto px-6 py-4">
          <div className="flex items-center space-x-4">
            <Button
              data-testid="back-home-btn"
              variant="ghost"
              size="sm"
              onClick={() => navigate("/")}
              className="text-zinc-400 hover:text-white"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Space Grotesk'}}>API Response Formatter</h1>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <Card className="bg-zinc-900/50 border-zinc-800 p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="grid w-full max-w-2xl mx-auto grid-cols-3 bg-zinc-800 border-zinc-700 mb-6">
              <TabsTrigger value="beautify" data-testid="beautify-tab">Beautify</TabsTrigger>
              <TabsTrigger value="flatten" data-testid="flatten-tab">Flatten</TabsTrigger>
              <TabsTrigger value="types" data-testid="types-tab">Generate Types</TabsTrigger>
            </TabsList>

            {/* Beautify Tab */}
            <TabsContent value="beautify" className="space-y-6">
              <div className="flex items-center space-x-4 mb-4">
                <Label htmlFor="format" className="text-zinc-400">Format:</Label>
                <select
                  data-testid="format-select"
                  id="format"
                  value={beautifyFormat}
                  onChange={(e) => setBeautifyFormat(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                >
                  <option value="json">JSON</option>
                  <option value="xml">XML</option>
                </select>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400">Input</Label>
                    {beautifyInput && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setBeautifyInput("")}
                        className="text-zinc-400 hover:text-white"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid="beautify-input"
                    value={beautifyInput}
                    onChange={(e) => setBeautifyInput(e.target.value)}
                    placeholder={beautifyFormat === "json" ? '{"key":"value"}' : '<root><item>value</item></root>'}
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400">Output</Label>
                    {beautifyOutput && (
                      <Button
                        data-testid="copy-beautify-btn"
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(beautifyOutput)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid="beautify-output"
                    value={beautifyOutput}
                    readOnly
                    placeholder="Beautified output will appear here..."
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
                  />
                </div>
              </div>

              <Button
                data-testid="beautify-btn"
                onClick={handleBeautify}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
              >
                Beautify {beautifyFormat.toUpperCase()}
              </Button>
            </TabsContent>

            {/* Flatten Tab */}
            <TabsContent value="flatten" className="space-y-6">
              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400">Nested JSON</Label>
                    {flattenInput && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setFlattenInput("")}
                        className="text-zinc-400 hover:text-white"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid="flatten-input"
                    value={flattenInput}
                    onChange={(e) => setFlattenInput(e.target.value)}
                    placeholder='{"user": {"name": "John", "address": {"city": "NYC"}}}'
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400">Flattened JSON</Label>
                    {flattenOutput && (
                      <Button
                        data-testid="copy-flatten-btn"
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(flattenOutput)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid="flatten-output"
                    value={flattenOutput}
                    readOnly
                    placeholder="Flattened output will appear here..."
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
                  />
                </div>
              </div>

              <Button
                data-testid="flatten-btn"
                onClick={handleFlatten}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
              >
                Flatten Structure
              </Button>
            </TabsContent>

            {/* Generate Types Tab */}
            <TabsContent value="types" className="space-y-6">
              <div className="flex items-center space-x-4 mb-4">
                <Label htmlFor="language" className="text-zinc-400">Target Language:</Label>
                <select
                  data-testid="language-select"
                  id="language"
                  value={typesLanguage}
                  onChange={(e) => setTypesLanguage(e.target.value)}
                  className="bg-zinc-800 border border-zinc-700 text-white rounded-md px-3 py-2"
                >
                  <option value="typescript">TypeScript</option>
                  <option value="go">Go</option>
                </select>
              </div>

              <div className="grid lg:grid-cols-2 gap-6">
                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400">JSON Sample</Label>
                    {typesInput && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setTypesInput("")}
                        className="text-zinc-400 hover:text-white"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid="types-input"
                    value={typesInput}
                    onChange={(e) => setTypesInput(e.target.value)}
                    placeholder='{"name": "John", "age": 30, "active": true}'
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
                  />
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <Label className="text-zinc-400">Generated Interface</Label>
                    {typesOutput && (
                      <Button
                        data-testid="copy-types-btn"
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(typesOutput)}
                        className="text-zinc-400 hover:text-white"
                      >
                        <Copy className="w-4 h-4 mr-2" />
                        Copy
                      </Button>
                    )}
                  </div>
                  <Textarea
                    data-testid="types-output"
                    value={typesOutput}
                    readOnly
                    placeholder="Generated types will appear here..."
                    className="bg-zinc-800 border-zinc-700 text-white font-mono text-sm min-h-[400px]"
                  />
                </div>
              </div>

              <Button
                data-testid="generate-types-btn"
                onClick={handleGenerateTypes}
                className="w-full bg-gradient-to-r from-orange-500 to-red-600 hover:from-orange-600 hover:to-red-700 text-white"
              >
                Generate {typesLanguage === "typescript" ? "TypeScript" : "Go"} Interface
              </Button>
            </TabsContent>
          </Tabs>
        </Card>
      </div>
    </div>
  );
}